import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import { IGoogleMap, IGoogleMapMarker } from '@models/IGoogleMap';
import GoogleMapIconTypesEnum from '@models/enums/GoogleMapIconTypesEnum';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

/*
In order to be able to use the new AdvancedMarkerElement markers, we needed a mapId set up
in Google Cloud Console (GCC) - https://console.cloud.google.com/google/maps-apis/overview.
First, we set up a map style ("Map Styles" section on the left sidebar in GCC)
where we can change colors and other aspects regarding the way the map looks like (we set up one style
for non-production and one style for production so we can have a more controlled way of deploying
any changes when required, but in the end they should be exactly the same). Then we go to the
"Map Management" section, create a new map ID and associate the map style to that ID. Again,
we will have 2 IDs (prod and non-prod). And then, this mapId will come from the CMS up to this component.
So whenever we will need to update something regarding the maps design, we will apply the changes to the corresponding
map style in GCC and then everything will apply automatically within the app (need to wait 1-2 minutes until changes are applied).
*/

type IconType = { svg: string; url?: never } | { url: string; svg?: never };

const iconTypes: Record<GoogleMapIconTypesEnum, IconType> = {
  [GoogleMapIconTypesEnum.Default]: {
    svg: '<svg width="45" class="transition-transform ease duration-300 hover:scale-95" height="55" viewBox="0 0 51 60" xmlns="http://www.w3.org/2000/svg"><path d="m27.2655 59.6402-.0037.0029c-1.0407.8092-2.4829.8092-3.5236 0l-.0029-.0022C8.26011 47.7292.75 36.525.75 26.0388.75 9.44883 13.922.75 25.5.75s24.75 8.69883 24.75 25.2888c0 10.4866-7.5108 21.7273-22.9845 33.6014Z" fill="#0A0203" stroke="#FD0" fill-opacity="1" stroke-width="1.5"></path></svg>',
  },
  [GoogleMapIconTypesEnum.DefaultSelected]: {
    svg: '<svg width="45" height="55" viewBox="0 0 51 60" xmlns="http://www.w3.org/2000/svg"><path d="m27.2655 59.6402-.0037.0029c-1.0407.8092-2.4829.8092-3.5236 0l-.0029-.0022C8.26011 47.7292.75 36.525.75 26.0388.75 9.44883 13.922.75 25.5.75s24.75 8.69883 24.75 25.2888c0 10.4866-7.5108 21.7273-22.9845 33.6014Z" fill="#FD0" stroke="#0A0203" fill-opacity="1" stroke-width="1.5"></path></svg>',
  },
  [GoogleMapIconTypesEnum.Small_UPS]: {
    url: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAAeCAYAAADZ7LXbAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAR7SURBVHgBrVZ9TJVVGP8958JFLl93giAIcWdBLdMypHSBshyhGN2sxpxjRI2stVz0h2t9zBbLrVpbuOyTObeWZjYiHJ/1RzrQrAz8QMW0gZZeBeKSuMuFe9/36bzvCxcv3DtuwG97t3N2znl+z3PO73melxAAVw7CkmAx28gT/gCDbURIB9hEQByDIhlQ5PwGMbmYcAVC7fJ6lC5LkuckrYBnsj0aH3Ct1eqJHCkhkF0eXAGGFf8b7GKmNulUrZe4wVIw/JdOMtoYuZJNeJUY6+R0HuYQzFwrCd+lkSZLj3Ed02PIBbR1MHocjL4BxiUH0D9orCnyDqECJpMxj40CMtMJ258TjrDpDP9xiXHoOKPtJKOrmxEqHP2ARzHGQUmOn2Xs/l7F7+dCNxwMU0hOX2Ts+kZFR9fsjU8hYWnzsxoVe+pUzDV0kusDwBu7FJy6MHfeTyF5p1pB55+McDlTZCCq/ISQShFGhN6xB0yKBxYtMFKrPcB1Lk4lWKO1R2f94f1INLxZbsKjuYTq71RU16pYeQ+hapsJ56SiyisVfPKaCfdm+nIXV/sYO3ar+O0M6868XyGQu1z41qv2qjjWaTgiEAK0CMcJ6g8zmo4wUmREH1SYMD8OWP8Q6QRXpfc796lobGPckUZTIwkV1VLW2nVYY0xYtYyweZ3AwgTD4P5mFftbVJ9jaQsp9EgC4cgJ4yqW3E641m+MNz1CSE4w1j3eib0zJnG5DcMJsox+1ajqD52SSKj7MAzlG/3NzphkHDeHgcEh4PkdCupbDeItTwiUFIrZk6SMSdntNubalVV+oWBfs/EmxfnkTyKrMJSxXEhNxLTQ9hfmGEYapJJSkybWWtvHZCstC9ILs1QXQY2VCdR8VIU9z4T1OQLRFkJacmCCV0oE4mONSDTJ/iJzoThfIP9BQvt5IOc+Y9+vZ4DoSL0rjgjZAU/FRZNebfeOhZp7P8GWTHr/aDrqn9l5WYSlGYQLl4Gt7ynoczL+7gWscaQnszVGkklbO79WkbxAVgxZtcKk2Y7Vy8n+7Y8ykWSWVtewJACcQ0Z50GC5pV+WvaWg1ymblXOCvO6Qqn932Qj//AudWMOqpUK+B/0sJGqWZRInzjcOaNI8280+gslw3vAnuBVdPewjSJR1bk22YFb5SxFRcLNTelq3rXTWavbDFpkrFjMfNBe6OnTLnjB+aU2WuPzCU3ND9OzjAvbVwuFlrtDmPjG7myLuFGRqaWxT0z8+oMqwJw5pdaisyHBAq02aIAIhxgK8WCzw5FrhHFU8D0dv8JzwI9Ew3DTPFkai6voA27X+XvtT6E3MnkfYukkgNlocVkaUZyIfc3ePr1GgA6PNUaVSfG/LnmHTektDa3Cy7CXEL28WlHEbXZNJWmkucH06eQ8FOyw7InlaosrkaLtGpkX2wzHGyCgQYQbWZhNKiwQvXkSanqrMw+aPaOPgYCBbQUn8yJqjnpaCf93Rq2acvghk3Q3Ex+G8zLTPw90Re4IZnxG8LTFF8o/zgLveskEjD/XcfxZczNxsQgllAAAAAElFTkSuQmCC',
  },
  [GoogleMapIconTypesEnum.Big_UPS]: {
    url: 'data:image/png;base64,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',
  },
};
const MAX_ZOOM = 16;

const InnerGoogleMap = forwardRef(
  (
    {
      boundsPadding,
      defaultIconType = GoogleMapIconTypesEnum.Default,
      selectedIconType = GoogleMapIconTypesEnum.Default,
      initialCenter,
      locations,
      mapId,
      zoomButtonsPositionClassName = 'bottom-2.5 right-2.5 md:bottom-7.5 md:right-7.5',
      onMarkerClick,
      onMapLoaded,
    }: IGoogleMap,
    ref
  ) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstance = useRef<google.maps.Map>();
    const mapMarkers = useRef<google.maps.marker.AdvancedMarkerElement[]>();
    const activeOpenInfoWindowMarkerRef =
      useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
    const infoWindow = useRef<google.maps.InfoWindow>();
    const { zoomInAriaLabel, zoomOutAriaLabel } = useAriaLabels();
    const [zoomLevel, setZoomLevel] = useState(-1);

    const centerMapOnMarker = (
      marker?: google.maps.marker.AdvancedMarkerElement
    ) => {
      const currentMarkerPosition = marker?.position;
      if (!currentMarkerPosition) return;
      mapInstance.current?.panTo(currentMarkerPosition);
    };

    const getCustomMarkerIcon = (iconData: IconType) => {
      if (iconData.url) {
        const iconImageElement = document.createElement('img');
        iconImageElement.src = iconData.url;

        return iconImageElement;
      }

      if (iconData.svg) {
        const parser = new DOMParser();
        const iconSVGElement = parser.parseFromString(
          iconData.svg,
          'image/svg+xml'
        ).documentElement;

        return iconSVGElement;
      }

      return null;
    };

    const updateMarkerAnimation = (markerContent: HTMLElement | null) => {
      markerContent?.classList.add('google-map-marker');
      markerContent?.addEventListener('animationend', () => {
        markerContent.classList.remove('google-map-marker');
      });
    };

    const setActiveMarkerToDefault = () => {
      const currentActiveMarker = mapMarkers.current?.find(
        (marker) => marker.zIndex === 1
      );
      if (currentActiveMarker) {
        currentActiveMarker.content = getCustomMarkerIcon(
          iconTypes[defaultIconType]
        );
        currentActiveMarker.zIndex = 0;
      }
    };

    const handleSelectedMarker = (
      selectedMarker: google.maps.marker.AdvancedMarkerElement
    ) => {
      centerMapOnMarker(selectedMarker);
      setActiveMarkerToDefault();

      const selectedIconContent = getCustomMarkerIcon(
        iconTypes[selectedIconType]
      );
      const isSelectedMarkerLoading = (
        selectedMarker.content as HTMLElement
      ).classList.contains('google-map-marker');
      if (isSelectedMarkerLoading) updateMarkerAnimation(selectedIconContent);

      // eslint-disable-next-line no-param-reassign
      selectedMarker.content = selectedIconContent;
      // eslint-disable-next-line no-param-reassign
      selectedMarker.zIndex = 1;
    };

    const getMapMarker = ({ latitude, longitude, title }: IGoogleMapMarker) =>
      mapMarkers.current?.find(
        (marker) =>
          marker.position?.lat === parseFloat(latitude || '0') &&
          marker.position?.lng === parseFloat(longitude || '0') &&
          marker.title === title
      );

    const handleMarkerInfoWindowOpen = (
      marker: IGoogleMapMarker,
      contentElement: HTMLDivElement
    ) => {
      const selectedMarker = getMapMarker(marker);
      if (!selectedMarker) return;

      const isActiveMarkerSelected =
        activeOpenInfoWindowMarkerRef.current?.position?.lat ===
          selectedMarker.position?.lat &&
        activeOpenInfoWindowMarkerRef.current?.position?.lng ===
          selectedMarker.position?.lng &&
        activeOpenInfoWindowMarkerRef.current?.title === selectedMarker.title;
      if (isActiveMarkerSelected || !infoWindow.current) return;

      infoWindow.current.setContent(contentElement);
      infoWindow.current.open(mapInstance.current, selectedMarker);
      activeOpenInfoWindowMarkerRef.current = selectedMarker;
    };

    const handleMarkerInfoWindowClose = () => {
      if (!infoWindow.current) return;

      infoWindow.current.close();
      setActiveMarkerToDefault();
      activeOpenInfoWindowMarkerRef.current = null;
    };

    useImperativeHandle(ref, () => ({
      selectMarkerOnMap: (marker: IGoogleMapMarker) => {
        const currentMarker = getMapMarker(marker);
        if (currentMarker) handleSelectedMarker(currentMarker);
      },
      openMarkerInfoWindow: handleMarkerInfoWindowOpen,
      closeMarkerInfoWindow: handleMarkerInfoWindowClose,
    }));

    const initMap = () => {
      if (!mapRef.current) return;

      mapInstance.current = new google.maps.Map(mapRef.current, {
        center: initialCenter,
        disableDefaultUI: true,
        maxZoom: MAX_ZOOM,
        mapId,
      });

      infoWindow.current = new google.maps.InfoWindow({
        pixelOffset: new google.maps.Size(0, -8, 'px', 'px'),
        headerDisabled: true,
        minWidth: 270,
      });

      mapMarkers.current = locations.map(({ name, latitude, longitude }) => {
        const customMarkerIcon = getCustomMarkerIcon(
          iconTypes[defaultIconType]
        );
        updateMarkerAnimation(customMarkerIcon);

        const marker = new google.maps.marker.AdvancedMarkerElement({
          position: { lat: parseFloat(latitude), lng: parseFloat(longitude) },
          map: mapInstance.current,
          title: name,
          content: customMarkerIcon,
        });

        marker.addListener('click', () => {
          handleSelectedMarker(marker);

          const markerTitle = marker.title;
          if (markerTitle) onMarkerClick(marker);
        });

        return marker;
      });

      if (mapMarkers.current.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        mapMarkers.current.forEach((marker) => {
          bounds.extend(marker.position as google.maps.LatLng);
        });
        mapInstance.current.fitBounds(bounds, boundsPadding);
      }

      google.maps.event.addListenerOnce(
        mapInstance.current,
        'tilesloaded',
        () => {
          const currentZoom = mapInstance.current?.getZoom() ?? 0;
          setZoomLevel(currentZoom);
        }
      );
    };

    useEffect(() => {
      const onInit = async () => {
        if (!google.maps.marker) await google.maps.importLibrary('marker');

        initMap();
        onMapLoaded?.();
      };

      onInit();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locations]);

    const handleZoomIn = () => {
      mapInstance.current?.setZoom(zoomLevel + 1);
      setZoomLevel(zoomLevel + 1);
    };

    const handleZoomOut = () => {
      mapInstance.current?.setZoom(zoomLevel - 1);
      setZoomLevel(zoomLevel - 1);
    };

    return (
      <>
        <div
          data-testid="google-map"
          ref={mapRef}
          className="relative h-full w-full"
        />

        <AnimatePresence>
          {zoomLevel > -1 && (
            <motion.div
              className={classNames(
                'absolute z-30 grid gap-[5px]',
                zoomButtonsPositionClassName
              )}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              data-testid="google-map-zoom-buttons-container"
            >
              <Button
                className="h-10 w-10 !p-0"
                onClick={handleZoomIn}
                aria-label={zoomInAriaLabel}
                dataTestId="google-map-zoom-in-button"
                disabled={zoomLevel === MAX_ZOOM}
              >
                <Icon
                  name="add"
                  size={IconSizeEnum.sm}
                  colorClassName={
                    zoomLevel === MAX_ZOOM ? 'text-grey-500' : 'text-black-100'
                  }
                />
              </Button>

              <Button
                className="h-10 w-10 !p-0"
                onClick={handleZoomOut}
                aria-label={zoomOutAriaLabel}
                dataTestId="google-map-zoom-out-button"
                disabled={zoomLevel === 0}
              >
                <Icon
                  name="minus"
                  size={IconSizeEnum.sm}
                  colorClassName={
                    zoomLevel === 0 ? 'text-grey-500' : 'text-black-100'
                  }
                />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }
);

export default InnerGoogleMap;
